import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../app/store/store';
import {
  fetchWorkShifts,
  createWorkShift,
  updateWorkShift,
  deleteWorkShift,
} from '../../../entities/helpdesk-settings/model';
import { helpdeskSettingsApi } from '../../../api/services/helpdesk-settings.service';
import type { WorkShift } from '../../../types/helpdeskSettingsInterfaces';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const shiftSummary = (s: WorkShift): string => {
  const days = (s.weekdays || []).slice().sort((a, b) => a - b);
  const dayStr = days.length ? days.map((d) => WEEKDAYS[d]).join(', ') : 'Любой день';
  return `${dayStr} · ${s.start_time?.slice(0, 5)}–${s.end_time?.slice(0, 5)}`;
};

interface ShiftModalProps {
  shift?: WorkShift | null;
  onClose: () => void;
  onSave: (data: Partial<WorkShift>) => void;
  onDelete?: () => void;
}

const ShiftModal: React.FC<ShiftModalProps> = ({ shift, onClose, onSave, onDelete }) => {
  const [name, setName] = useState(shift?.name || '');
  const [weekdays, setWeekdays] = useState<number[]>(shift?.weekdays || []);
  const [start, setStart] = useState((shift?.start_time || '09:00').slice(0, 5));
  const [end, setEnd] = useState((shift?.end_time || '18:00').slice(0, 5));
  const [operatorIds, setOperatorIds] = useState<number[]>(
    shift?.operators?.map((o) => o.id) || [],
  );
  const [operators, setOperators] = useState<
    { id: number; username: string; first_name: string; last_name: string; full_name: string }[]
  >([]);

  useEffect(() => {
    helpdeskSettingsApi
      .getAvailableOperators()
      .then((r) => setOperators(r.data))
      .catch(() => setOperators([]));
  }, []);

  const toggleDay = (d: number) =>
    setWeekdays((w) => (w.includes(d) ? w.filter((x) => x !== d) : [...w, d]));
  const toggleOp = (id: number) =>
    setOperatorIds((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));

  const opName = (o: {
    first_name: string;
    last_name: string;
    full_name: string;
    username: string;
  }) => o.full_name || `${o.last_name} ${o.first_name}`.trim() || o.username;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      weekdays,
      start_time: start,
      end_time: end,
      operator_ids: operatorIds,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">
            {shift ? 'Изменить рабочую смену' : 'Добавить рабочую смену'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Название <span className="text-red-500">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Напр.: Пятница вечер"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Дни недели</label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((d, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleDay(i)}
                  className={`w-10 h-9 rounded-lg text-sm font-medium border transition-colors ${
                    weekdays.includes(i)
                      ? 'bg-[#e3282a] text-white border-[#e3282a]'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1">Пусто = любой день</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Начало</label>
              <input
                type="time"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Конец</label>
              <input
                type="time"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Операторы в смене</label>
            <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg divide-y divide-gray-100">
              {operators.length === 0 ? (
                <p className="text-xs text-gray-400 px-3 py-3">Нет доступных операторов</p>
              ) : (
                operators.map((o) => (
                  <label
                    key={o.id}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={operatorIds.includes(o.id)}
                      onChange={() => toggleOp(o.id)}
                      className="w-4 h-4 rounded border-gray-300 focus:ring-red-400"
                    />
                    <span className="text-sm text-[#2d2c2c]">{opName(o)}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <div>
              {shift && onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800"
                >
                  Удалить
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={!name.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] disabled:opacity-50"
              >
                {shift ? 'Сохранить' : 'Добавить'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const WorkShiftSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { workShifts, workShiftsStatus } = useSelector(
    (state: RootState) => state.helpdeskSettings,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WorkShift | null>(null);

  useEffect(() => {
    if (workShiftsStatus === 'idle') dispatch(fetchWorkShifts());
  }, [dispatch, workShiftsStatus]);

  const handleSave = useCallback(
    async (data: Partial<WorkShift>) => {
      if (editing) await dispatch(updateWorkShift({ id: editing.id, data }));
      else await dispatch(createWorkShift(data));
      setModalOpen(false);
      setEditing(null);
    },
    [dispatch, editing],
  );

  const handleDelete = useCallback(async () => {
    if (!editing) return;
    await dispatch(deleteWorkShift(editing.id));
    setModalOpen(false);
    setEditing(null);
  }, [dispatch, editing]);

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-base font-semibold text-[#2d2c2c]">Рабочие смены</h2>
        <button
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] transition-colors"
        >
          + Добавить смену
        </button>
      </div>
      <p className="text-sm text-gray-400 mb-4">
        Именованная смена с часами работы и списком операторов. Используется как шаблон и в маршрутизации.
      </p>

      {workShifts.length > 0 ? (
        <div>
          {workShifts.map((s) => (
            <div
              key={s.id}
              onClick={() => {
                setEditing(s);
                setModalOpen(true);
              }}
              className="flex items-center justify-between py-3.5 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded transition-colors"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#2d2c2c]">{s.name}</p>
                <p className="text-xs text-gray-400 truncate">
                  {shiftSummary(s)} ·{' '}
                  {s.operators?.length
                    ? `${s.operators.length} операт.`
                    : 'без операторов'}
                </p>
              </div>
              <span
                className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ml-4 ${
                  s.is_active
                    ? 'bg-green-50 text-green-600 border border-green-200'
                    : 'bg-gray-50 text-gray-500 border border-gray-200'
                }`}
              >
                {s.is_active ? 'Активна' : 'Неактивна'}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400 text-center py-8">
          Рабочих смен пока нет. Нажмите «Добавить смену».
        </p>
      )}

      {modalOpen && (
        <ShiftModal
          shift={editing}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          onSave={handleSave}
          onDelete={editing ? handleDelete : undefined}
        />
      )}
    </div>
  );
};

export default WorkShiftSettings;
